import { useEffect, useMemo, useState } from 'react';
import { Sprout, Send } from 'lucide-react';
import { request } from '../../api/client';
import { AI_LANGUAGES } from '../../lib/aiLanguages';
import { ChatShell, type ChatMessage } from '../../components/ChatShell';
import { CropCombobox, LangSelect } from '../../components/ChatControls';

interface Product { name: string; sku: string; imageUrl: string | null }

const LANGS = AI_LANGUAGES;

const PLACEHOLDER: Record<string, { crop: string; problem: string; ask: string; hint: string }> = {
  en: { crop: 'Choose or type your crop…', problem: 'Optional: describe the problem (pest, spots, yellowing…)', ask: 'Get advice', hint: 'Pick your crop and I’ll suggest suitable Green Bay Bio products — what they are, why they help, and how to use them.' },
  hi: { crop: 'अपनी फसल चुनें या लिखें…', problem: 'वैकल्पिक: समस्या बताएं (कीट, धब्बे, पीलापन…)', ask: 'सलाह लें', hint: 'अपनी फसल चुनें — मैं उपयुक्त Green Bay Bio उत्पाद बताऊँगा: क्या, क्यों और कैसे उपयोग करें।' },
  mr: { crop: 'तुमचे पीक निवडा किंवा लिहा…', problem: 'ऐच्छिक: समस्या सांगा (कीड, ठिपके, पिवळेपणा…)', ask: 'सल्ला घ्या', hint: 'तुमचे पीक निवडा — मी योग्य Green Bay Bio उत्पादने सुचवेन: काय, का आणि कसे वापरावे.' },
  bn: { crop: "আপনার ফসল বেছে নিন বা লিখুন…", problem: "ঐচ্ছিক: সমস্যাটি বর্ণনা করুন (পোকা, দাগ, পাতা হলুদ হয়ে যাওয়া…)", ask: "পরামর্শ নিন", hint: "আপনার ফসল বেছে নিন, আমি উপযুক্ত Green Bay Bio পণ্যের পরামর্শ দেব — সেগুলি কী, কেন সহায়ক, এবং কীভাবে ব্যবহার করবেন।" },
  or: { crop: "ଆପଣଙ୍କ ଫସଲ ବାଛନ୍ତୁ କିମ୍ବା ଟାଇପ୍ କରନ୍ତୁ…", problem: "ଐଚ୍ଛିକ: ସମସ୍ୟା ବର୍ଣ୍ଣନା କରନ୍ତୁ (କୀଟ, ଦାଗ, ହଳଦିଆ ପଡ଼ିବା…)", ask: "ପରାମର୍ଶ ନିଅନ୍ତୁ", hint: "ଆପଣଙ୍କ ଫସଲ ବାଛନ୍ତୁ, ମୁଁ ଉପଯୁକ୍ତ Green Bay Bio ଉତ୍ପାଦ ପରାମର୍ଶ ଦେବି — ସେଗୁଡ଼ିକ କ'ଣ, କାହିଁକି ସହାୟକ, ଏବଂ କିପରି ବ୍ୟବହାର କରିବେ।" },
  te: { crop: "మీ పంటను ఎంచుకోండి లేదా టైప్ చేయండి…", problem: "ఐచ్ఛికం: సమస్యను వివరించండి (చీడపీడలు, మచ్చలు, పసుపు రంగు…)", ask: "సలహా పొందండి", hint: "మీ పంటను ఎంచుకోండి, తగిన Green Bay Bio ఉత్పత్తులను నేను సూచిస్తాను — అవి ఏమిటి, ఎందుకు సహాయపడతాయి, వాటిని ఎలా వాడాలి అన్నది తెలియజేస్తాను." },
  ta: { crop: "உங்கள் பயிரைத் தேர்ந்தெடுக்கவும் அல்லது தட்டச்சு செய்யவும்…", problem: "விருப்பத் தேர்வு: பிரச்சினையை விவரிக்கவும் (பூச்சி, புள்ளிகள், மஞ்சளாதல்…)", ask: "ஆலோசனை பெறுங்கள்", hint: "உங்கள் பயிரைத் தேர்ந்தெடுங்கள், பொருத்தமான Green Bay Bio தயாரிப்புகளை நான் பரிந்துரைப்பேன் — அவை என்ன, ஏன் உதவுகின்றன, எப்படிப் பயன்படுத்துவது என்பதையும் சொல்கிறேன்." },
  gu: { crop: "તમારો પાક પસંદ કરો અથવા લખો…", problem: "વૈકલ્પિક: સમસ્યા જણાવો (જીવાત, ડાઘ, પીળાશ…)", ask: "સલાહ મેળવો", hint: "તમારો પાક પસંદ કરો અને હું તમને યોગ્ય Green Bay Bio ઉત્પાદનો સૂચવીશ — તે શું છે, શા માટે ઉપયોગી છે, અને તેનો ઉપયોગ કેવી રીતે કરવો." },
  pa: { crop: "ਆਪਣੀ ਫ਼ਸਲ ਚੁਣੋ ਜਾਂ ਲਿਖੋ…", problem: "ਵਿਕਲਪਿਕ: ਸਮੱਸਿਆ ਬਾਰੇ ਦੱਸੋ (ਕੀੜੇ, ਧੱਬੇ, ਪੀਲਾਪਣ…)", ask: "ਸਲਾਹ ਲਓ", hint: "ਆਪਣੀ ਫ਼ਸਲ ਚੁਣੋ ਅਤੇ ਮੈਂ ਤੁਹਾਨੂੰ ਢੁੱਕਵੇਂ Green Bay Bio ਉਤਪਾਦ ਸੁਝਾਵਾਂਗਾ — ਉਹ ਕੀ ਹਨ, ਕਿਉਂ ਲਾਭਦਾਇਕ ਹਨ, ਅਤੇ ਇਨ੍ਹਾਂ ਨੂੰ ਕਿਵੇਂ ਵਰਤਣਾ ਹੈ।" },
  kn: { crop: "ನಿಮ್ಮ ಬೆಳೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ ಅಥವಾ ಟೈಪ್ ಮಾಡಿ…", problem: "ಐಚ್ಛಿಕ: ಸಮಸ್ಯೆಯನ್ನು ವಿವರಿಸಿ (ಕೀಟ, ಚುಕ್ಕೆಗಳು, ಹಳದಿಯಾಗುವಿಕೆ…)", ask: "ಸಲಹೆ ಪಡೆಯಿರಿ", hint: "ನಿಮ್ಮ ಬೆಳೆಯನ್ನು ಆರಿಸಿ, ನಾನು ಸೂಕ್ತವಾದ Green Bay Bio ಉತ್ಪನ್ನಗಳನ್ನು ಸೂಚಿಸುತ್ತೇನೆ — ಅವು ಏನು, ಅವು ಏಕೆ ಸಹಾಯ ಮಾಡುತ್ತವೆ, ಮತ್ತು ಅವುಗಳನ್ನು ಹೇಗೆ ಬಳಸಬೇಕು ಎಂಬುದನ್ನು ತಿಳಿಸುತ್ತೇನೆ." },
};
const ph = (l: string) => PLACEHOLDER[l] ?? PLACEHOLDER.en;

/**
 * Public crop advisor for the marketing site + farmers (no login). Farmer picks a crop → price-free
 * product recommendations (what/why/how), in their language. Talks to POST /public/ai/crop-advisor.
 * Never shows a price (enforced server-side too).
 */
export function PublicCropBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [crops, setCrops] = useState<string[]>([]);
  const [crop, setCrop] = useState('');
  const [problem, setProblem] = useState('');
  const [lang, setLang] = useState('en');
  const [loading, setLoading] = useState(false);
  const t = useMemo(() => ph(lang), [lang]);

  useEffect(() => {
    if (!open || crops.length) return;
    request<any>({ url: '/public/ai/crops' })
      .then((r) => setCrops(Array.isArray(r) ? r : r?.data ?? []))
      .catch(() => {});
  }, [open, crops.length]);

  const ask = async () => {
    const c = crop.trim();
    if (!c || loading) return;
    setMessages((m) => [...m, { role: 'user', content: problem.trim() ? `${c} — ${problem.trim()}` : c }]);
    setLoading(true);
    try {
      const r = await request<any>({ url: '/public/ai/crop-advisor', method: 'POST', data: { crop: c, problem: problem.trim() || undefined, lang } });
      const body = r?.data ?? r;
      const products: Product[] = body?.products ?? [];
      setMessages((m) => [...m, {
        role: 'assistant',
        content: body?.reply || '…',
        extra: products.length ? <ProductCards products={products} /> : undefined,
      }]);
      setProblem('');
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, the advisor is unavailable right now. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const composer = (
    <div className="space-y-1.5">
      <div className="flex gap-1.5">
        <CropCombobox
          value={crop}
          onChange={setCrop}
          options={crops}
          placeholder={t.crop}
          onSubmit={ask}
          ariaLabel="Crop"
        />
        <LangSelect value={lang} onChange={setLang} options={LANGS} ariaLabel="Language" className="w-26" />
      </div>
      <div className="flex gap-1.5">
        <input
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ask(); } }}
          placeholder={t.problem}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-brand-500"
        />
        <button
          type="button"
          disabled={loading || !crop.trim()}
          onClick={ask}
          className="flex shrink-0 items-center gap-1 rounded-lg bg-brand-600 px-3 py-2 text-[12px] font-medium text-white transition hover:bg-brand-700 disabled:opacity-40"
        >
          <Send size={14} /> {t.ask}
        </button>
      </div>
    </div>
  );

  return (
    <ChatShell
      title="Krishi Mitra · Crop Advisor"
      subtitle="Free product advice for your crop"
      open={open}
      onToggle={setOpen}
      messages={messages}
      loading={loading}
      fabAriaLabel="Open crop advisor"
      emptyHint={
        <div className="flex flex-col items-center gap-2">
          <Sprout size={26} className="text-brand-500" />
          <p>{t.hint}</p>
        </div>
      }
      composer={composer}
    />
  );
}

function ProductCards({ products }: { products: Product[] }) {
  return (
    <div className="mt-1.5 flex max-w-[86%] flex-col gap-1.5">
      {products.map((p) => (
        <div key={p.sku} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">
          {p.imageUrl
            ? <img src={p.imageUrl} alt={p.name} className="size-9 shrink-0 rounded-lg object-cover" loading="lazy" />
            : <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600"><Sprout size={16} /></div>}
          <span className="truncate text-[12px] font-medium text-gray-800">{p.name}</span>
        </div>
      ))}
      {/* Deliberately no price shown — pricing is via a Green Bay Bio dealer. */}
    </div>
  );
}
